import {
    CollectionApi,
    CoreFetch,
    GetProviderCollectionDraftInvoicesRequest,
    GetProviderCollectionInvoiceSummaryRequest,
    GetProviderCollectionInvoicesRequest,
    InvoiceDraftApproveActionRequest,
    InvoiceDraftCreateRequest,
    InvoiceDraftDenyActionRequest,
    ListProviderCollectionPaymentsRequest,
    ListProviderCollectionPaymentSourcesRequest,
    ProviderAllocateCollectionPaymentSourceListPayload,
    ProviderCollectionInvoice,
    ProviderCollectionInvoiceDraft,
    ProviderCollectionInvoiceDraftPreview,
    ProviderCollectionInvoiceIntegrationItem,
    ProviderCollectionInvoiceSummaryItem,
    ProviderCollectionPayment,
    ProviderCollectionPaymentSourceWithAllocationInfo,
} from '@enter/core';
import { IItemListing } from './root.store';
import { fetch, parseResponse } from './util';

export class CollectionInvoiceService {
    public async fetchIntegrations(): Promise<ProviderCollectionInvoiceIntegrationItem[]> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).getProviderCollectionInvoiceIntegrationsRaw();
        return await parseResponse(res);
    }

    public async fetchSummary(
        req: GetProviderCollectionInvoiceSummaryRequest,
    ): Promise<ProviderCollectionInvoiceSummaryItem> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).getProviderCollectionInvoiceSummaryRaw(req);
        return await parseResponse(res);
    }

    public async fetchDraftInvoices(
        req: GetProviderCollectionDraftInvoicesRequest,
    ): Promise<IItemListing<ProviderCollectionInvoiceDraft> | undefined> {
        const res = await fetch(req, r =>
            CoreFetch<CollectionApi>(CollectionApi).getProviderCollectionDraftInvoicesRaw(r),
        );
        if (!res) {
            return undefined;
        }
        return {
            items: await parseResponse(res),
            limit: Number(await res.raw.headers.get('pagination-limit')),
            offset: Number(await res.raw.headers.get('pagination-offset')),
            total: Number(await res.raw.headers.get('pagination-total')),
        };
    }

    public async fetchInvoices(
        req: GetProviderCollectionInvoicesRequest,
    ): Promise<IItemListing<ProviderCollectionInvoice> | undefined> {
        const res = await fetch(req, r =>
            CoreFetch<CollectionApi>(CollectionApi).getProviderCollectionInvoicesRaw(r),
        );
        if (!res) {
            return undefined;
        }
        return {
            items: await parseResponse(res),
            limit: Number(await res.raw.headers.get('pagination-limit')),
            offset: Number(await res.raw.headers.get('pagination-offset')),
            total: Number(await res.raw.headers.get('pagination-total')),
        };
    }

    public async previewDraftInvoice(
        invoiceDraftCreateRequest: InvoiceDraftCreateRequest,
    ): Promise<ProviderCollectionInvoiceDraftPreview> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).postProviderCreateDraftInvoicePreviewRaw({
            invoiceDraftCreateRequest,
        });
        return await parseResponse(res);
    }

    public async createDraftInvoice(
        invoiceDraftCreateRequest: InvoiceDraftCreateRequest,
    ): Promise<ProviderCollectionInvoiceDraft> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).postProviderCreateDraftInvoiceRaw({
            invoiceDraftCreateRequest,
        });
        return await parseResponse(res);
    }

    public async approveDraftInvoice(
        id: string,
        invoiceDraftApproveActionRequest: InvoiceDraftApproveActionRequest,
    ): Promise<ProviderCollectionInvoice> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).postProviderApproveDraftInvoiceRaw({
            id,
            invoiceDraftApproveActionRequest,
        });
        return await parseResponse(res);
    }

    public async denyDraftInvoice(
        id: string,
        invoiceDraftDenyActionRequest: InvoiceDraftDenyActionRequest,
    ): Promise<ProviderCollectionInvoiceDraft> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).postProviderDenyDraftInvoiceRaw({
            id,
            invoiceDraftDenyActionRequest,
        });
        return await parseResponse(res);
    }

    public async fetchPaymentSources(
        req: ListProviderCollectionPaymentSourcesRequest,
    ): Promise<ProviderCollectionPaymentSourceWithAllocationInfo[]> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPaymentSourcesRaw(req);
        return (await parseResponse(res)) || [];
    }

    public async fetchPayments(req: ListProviderCollectionPaymentsRequest): Promise<ProviderCollectionPayment[]> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPaymentsRaw(req);
        return (await parseResponse(res)) || [];
    }

    public async allocatePaymentSource(
        id: string,
        providerAllocateCollectionPaymentSourceListPayload: ProviderAllocateCollectionPaymentSourceListPayload,
    ): Promise<ProviderCollectionPaymentSourceWithAllocationInfo> {
        const res = await CoreFetch<CollectionApi>(CollectionApi).postProviderAllocateCollectionPaymentSourceRaw({
            id,
            providerAllocateCollectionPaymentSourceListPayload,
        });
        return await parseResponse(res);
    }
}
